
import React, { useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { CheckCircle, Heart } from 'lucide-react';
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

// Define the schema with required fields to match the database requirements
const feedbackFormSchema = z.object({
  first_name: z.string().min(2, { message: "El nombre debe tener al menos 2 caracteres" }),
  last_name: z.string().min(2, { message: "El apellido debe tener al menos 2 caracteres" }),
  email: z.string().email({ message: "Por favor ingresa un correo válido" }),
  message: z.string().min(10, { message: "Tu mensaje debe tener al menos 10 caracteres" })
});

type FeedbackFormValues = z.infer<typeof feedbackFormSchema>;

const UserFeedback = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackFormSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      message: ''
    },
  });

  const onSubmit = async (values: FeedbackFormValues) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from('user_feedback').insert({
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        message: values.message
      });
      
      if (error) throw error;
      
      setIsSubmitted(true);
      toast({
        title: "¡Gracias por tu mensaje!",
        description: "Hemos recibido tus comentarios correctamente.",
      });
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      toast({
        title: "Error",
        description: "No se pudo enviar tu mensaje. Por favor intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-cream">
        {/* Clean Header */}
        <div className="bg-white py-16 md:py-20">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-navy mb-4">
              ¡Mensaje Recibido!
            </h1>
            <p className="text-lg text-coolGray max-w-2xl mx-auto">
              Gracias por tomarte el tiempo de contactarnos.
            </p>
          </div>
        </div>
        
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-lg mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-emerald/10 rounded-full p-4">
                <CheckCircle className="h-12 w-12 text-emerald" />
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-navy mb-4">
              Gracias por tu mensaje
            </h2>
            
            <p className="text-coolGray mb-8 leading-relaxed">
              Apreciamos tus comentarios y los tomamos en cuenta para mejorar nuestro servicio. 
              Tu experiencia es importante para nosotros.
            </p>
            
            <Button 
              onClick={() => setIsSubmitted(false)}
              variant="outline"
              className="w-full md:w-auto"
            >
              Enviar otro mensaje
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Clean Header Section */}
      <div className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-primary/10 rounded-full p-3">
              <Heart className="h-8 w-8 text-primary" />
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-navy mb-4">
            Queremos Escucharte
          </h1>
          
          <p className="text-lg text-coolGray max-w-2xl mx-auto leading-relaxed">
            Tus comentarios nos ayudan a mejorar la experiencia para todos.
          </p>
        </div>
      </div>
      
      {/* Form Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Name Fields Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-navy font-medium">Nombre</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Tu nombre" 
                            className="rounded-lg border-gray-200 focus:border-primary focus:ring-primary/20 py-3"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-navy font-medium">Apellido</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Tu apellido" 
                            className="rounded-lg border-gray-200 focus:border-primary focus:ring-primary/20 py-3"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-navy font-medium">Correo electrónico</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="tucorreo@ejemplo.com" 
                          type="email" 
                          className="rounded-lg border-gray-200 focus:border-primary focus:ring-primary/20 py-3"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-navy font-medium">Tu mensaje</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Comparte tus comentarios, sugerencias o experiencias aquí..." 
                          className="min-h-[140px] rounded-lg border-gray-200 focus:border-primary focus:ring-primary/20 py-3 resize-none"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="pt-4">
                  <Button 
                    type="submit" 
                    className="w-full md:w-auto md:min-w-[200px] bg-primary hover:bg-primary/90 text-white font-medium py-3 px-8 rounded-lg transition-all duration-200 hover:shadow-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Enviando..." : "Enviar Comentario"}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
          
          {/* Additional Info */}
          <div className="mt-8 text-center">
            <p className="text-sm text-coolGray">
              Valoramos tu privacidad. Tu información solo será utilizada para mejorar nuestros servicios.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserFeedback;
